import { Text } from '@mantine/core';
import React from 'react';
import { useSelector } from 'react-redux';
import { useMyForm } from '../../../../../../../react-commons/hooks/useMyForm';
import { QueryBaseComponent } from '../../../../../../../react-commons/query/QueryBaseComponent';
import { DisplayComponentBaseView } from '../../../../../../../react-commons/views/DisplayComponentBaseView';
import { ResourceType } from '../../../../../resource/enumerations/ResourceType';
import {
    defaultUseCaseResource,
    UseCaseMutationResourceSchema,
    UseCasePropertyValidation,
    useUseCaseActions,
} from '../../../../../resource/resources/artifacts/use_case/useCaseResource';
import { Form_DisplayComponentBaseView } from '../../../../reusable-components/form-components/Form_DisplayComponentBaseView';
import { SingleParentedOverviewComponent } from '../../../../reusable-components/resource-overviews/SingleParentedOverviewComponent';
import { ResourceValidationTitleBar } from '../../../../reusable-components/validation/ResourceValidationTitleBar';
import { OverviewComponentPropertyValidationInformation } from '../../../../util/overviewUtils';
import { selectNoScopeValidationScope, selectReadOnly } from '../../../../util/useSelectorFunctions';
import { UseCaseContent } from './UseCaseContent';

const UseCaseOverviewContent = ({ id, displayedInOverview }: { id: string; displayedInOverview: boolean }) => {
    const readOnly = useSelector(selectReadOnly);

    const useCaseActions = useUseCaseActions();
    const useCaseQuery = useCaseActions.useGetResource(id);
    const updateUseCase = useCaseActions.useUpdateResource();

    const { form, formNames, onSubmit } = useMyForm({
        schema: UseCaseMutationResourceSchema,
        defaultValues: defaultUseCaseResource,
        submitHandler: async (data) => {
            await updateUseCase.mutateAsync({
                resource: data,
                id: data.id,
            });
        },
        reactiveValues: useCaseQuery.data,
    });

    return (
        <QueryBaseComponent name="UseCase" query={useCaseQuery}>
            <Form_DisplayComponentBaseView onSubmit={onSubmit} form={form} canSubmitDefaultValue={false}>
                <UseCaseContent
                    displayedInOverview={displayedInOverview}
                    form={form}
                    formNames={formNames}
                    readOnly={readOnly}
                    id={id}
                />
            </Form_DisplayComponentBaseView>
        </QueryBaseComponent>
    );
};

export const UseCaseOverviewComponent = ({ parentId }: { parentId: string }) => {
    const validationScope = useSelector(selectNoScopeValidationScope);

    const useCaseActions = useUseCaseActions();

    const parametersQuery = useCaseActions.useGetAllResources(parentId);

    const resourceType = ResourceType.USE_CASE;

    return (
        <QueryBaseComponent name="UseCases" query={parametersQuery}>
            <SingleParentedOverviewComponent
                label={"Use Cases"}
                resourceQuery={parametersQuery}
                content={(id, displayedInOverview) => (
                    <UseCaseOverviewContent id={id} displayedInOverview={displayedInOverview} />
                )}
                propertyValidationInformation={{
                    namePropertyIdentifier: UseCasePropertyValidation.NAME,
                    resourceType,
                }}
                validationScope={validationScope}
                parentId={parentId}
            />
        </QueryBaseComponent>
    );
};
